/**
 * Shared keyboard navigation utilities for dropdown components
 */

export interface DropdownKeyboardNavigationOptions {
    /** Current index of the focused item */
    currentIndex: number;
    /** Total number of items in the dropdown */
    itemCount: number;
    /** Optional: Element to focus when navigating back from the first item */
    returnElement?: HTMLElement | null;
}

/**
 * Creates a keyboard event handler for dropdown buttons
 * Supports: Arrow Up/Down, Home, End, Shift+Tab (to return element)
 */
export const createDropdownButtonKeyHandler = (
    options: DropdownKeyboardNavigationOptions,
    onFocusChange: (newIndex: number) => void,
) => {
    return (event: KeyboardEvent) => {
        const { currentIndex, itemCount, returnElement } = options;

        if (itemCount === 0) return;

        switch (event.key) {
            case 'ArrowDown':
                event.preventDefault();
                onFocusChange((currentIndex + 1) % itemCount);
                break;

            case 'ArrowUp':
                event.preventDefault();

                // If at first item and returnElement exists, go back to it
                if (currentIndex === 0 && returnElement) {
                    returnElement.focus();
                } else {
                    onFocusChange(currentIndex === 0 ? itemCount - 1 : currentIndex - 1);
                }
                break;

            case 'Home':
                event.preventDefault();
                onFocusChange(0);
                break;

            case 'End':
                event.preventDefault();
                onFocusChange(itemCount - 1);
                break;

            case 'Tab':
                // If shift+tab on first item and returnElement exists, go back to it
                if (event.shiftKey && currentIndex === 0 && returnElement) {
                    event.preventDefault();
                    returnElement.focus();
                }
                break;
        }
    };
};

/**
 * Focus a button at a specific index within a container
 */
export const focusButtonAtIndex = (
    containerElement: HTMLElement,
    index: number,
    options?: { scroll?: boolean; behavior?: ScrollBehavior },
) => {
    const button = containerElement.querySelector(
        `button:nth-child(${index + 1})`,
    ) as HTMLButtonElement;

    if (button) {
        button.focus();

        if (options?.scroll !== false) {
            button.scrollIntoView({
                block: 'nearest',
                behavior: options?.behavior || 'smooth',
            });
        }
    }
};

/**
 * Creates a keyboard event handler for input fields that moves focus to the first dropdown button
 * when arrow keys are pressed
 */
export const createInputToDropdownKeyHandler = (
    dropdownId: string,
    isOpen: boolean,
    onClose?: () => void,
) => {
    return (event: KeyboardEvent) => {
        if (isOpen && (event.key === 'ArrowDown' || event.key === 'ArrowUp')) {
            event.preventDefault();
            const firstButton = document.querySelector(
                `#${dropdownId} button`,
            ) as HTMLButtonElement;
            firstButton?.focus();
        } else if (event.key === 'Escape' && isOpen && onClose) {
            event.preventDefault();
            onClose();
        }
    };
};
